import { ReadScopedStorageItemInfoResult, ScopedStorageItemInfo, WriteScopedStorageItemInfoParam } from '../types/obs';
import { ensureOBSPresence } from '../utils/ensureOBSPresence';
import { promisefy } from '../utils/promisefy';

interface StorageAdapter {
  readScopedStorageJsonItem<T>(
    info: ScopedStorageItemInfo,
    callback: (result: ReadScopedStorageItemInfoResult<T> | null) => void
  ): void;
  writeScopedStorageJsonItem<T>(
    info: WriteScopedStorageItemInfoParam<T>,
    callback: (result: ReadScopedStorageItemInfoResult<T> | null) => void
  ): void;
  removeScopedStorageJsonItem<T = unknown>(
    info: ScopedStorageItemInfo,
    callback: (result: ReadScopedStorageItemInfoResult<T> | null) => void
  ): void;
  getAllScopedStorageJsonItems<T>(
    info: ScopedStorageItemInfo,
    callback: (result: ReadScopedStorageItemInfoResult<T>[] | null) => void
  ): void;
}

function getScopedStorageKey(info: ScopedStorageItemInfo): string {
  return `${info.scope}:${info.container}:${info.item}`;
}

export function scopedStorageLocalStorageAdapter(): StorageAdapter {
  async function writeScopedStorageJsonItem<T>(
    info: WriteScopedStorageItemInfoParam<T>,
    callback: (result: ReadScopedStorageItemInfoResult<T> | null) => void
  ) {
    const key = getScopedStorageKey(info);
    localStorage.setItem(key, JSON.stringify(info));
    callback(info);
  }

  async function readScopedStorageJsonItem<T>(
    info: ScopedStorageItemInfo,
    callback: (result: ReadScopedStorageItemInfoResult<T> | null) => void
  ) {
    const key = getScopedStorageKey(info);
    const item = localStorage.getItem(key);

    if (!item) {
      callback(null);
      return;
    }

    try {
      const content = JSON.parse(item);
      callback(content);
    } catch (error) {
      callback(null);
    }
  }

  async function removeScopedStorageJsonItem<T = unknown>(
    info: ScopedStorageItemInfo,
    callback: (result: ReadScopedStorageItemInfoResult<T> | null) => void
  ) {
    const key = getScopedStorageKey(info);

    readScopedStorageJsonItem<T>(info, (result) => {
      if (!result) {
        callback(null);
        return;
      }

      localStorage.removeItem(key);
      callback(result);
    });
  }

  async function getAllScopedStorageJsonItems<T>(
    info: ScopedStorageItemInfo,
    callback: (result: ReadScopedStorageItemInfoResult<T>[] | null) => void
  ) {
    readScopedStorageJsonItem<T>(info, (result) => {
      if (!result) {
        callback(null);
        return;
      }

      callback([result]);
    });
  }

  return {
    writeScopedStorageJsonItem,
    readScopedStorageJsonItem,
    removeScopedStorageJsonItem,
    getAllScopedStorageJsonItems,
  };
}

async function locked<T>(key: ScopedStorageItemInfo, exclusive: boolean, callback: () => Promise<T>): Promise<T> {
  return await navigator.locks.request(
    `${key.scope}/${key.container}/${key.item}`,
    { mode: exclusive ? 'exclusive' : 'shared' },
    async () => {
      return await callback();
    }
  ) as T;
}

class ScopedStorageService {
  async getAllScopedStorageJsonItems<T>(
    info: Omit<ScopedStorageItemInfo, 'item'>
  ): Promise<ReadScopedStorageItemInfoResult<T>[]> {
    await ensureOBSPresence();

    return await locked({ ...info, item: '' }, false, async () => {
      const result = await promisefy(window.host.getAllScopedStorageJsonItems<T>)(info);

      if (!result) {
        return [];
      }

      return result;
    });
  }

  async writeScopedStorageJsonItem<T>(
    info: WriteScopedStorageItemInfoParam<T>
  ): Promise<ReadScopedStorageItemInfoResult<T>> {
    await ensureOBSPresence();

    return await locked(info, true, async () => {
      const result = await promisefy(window.host.writeScopedStorageJsonItem<T>)(info);

      if (!result) {
        console.warn('Failed to write scoped storage item: ' + JSON.stringify(info));

        throw new Error('Failed to write scoped storage item');
      }

      return result;
    });
  }

  async readScopedStorageJsonItem<T>(info: ScopedStorageItemInfo): Promise<ReadScopedStorageItemInfoResult<T> | null> {
    await ensureOBSPresence();

    return await locked(info, false, async () => {
      const result = await promisefy(window.host.readScopedStorageJsonItem<T>)(info);

      if (!result) {
        // throw new TransientError('Failed to read scoped storage item');
        return null;
      }

      return result;
    });
  }

  async removeScopedStorageJsonItem(info: ScopedStorageItemInfo): Promise<void> {
    await ensureOBSPresence();

    return await locked(info, true, async () => {
      const result = await promisefy(window.host.removeScopedStorageJsonItem)(info);

      if (!result) {
        console.warn('Failed to remove scoped storage item: ', info);
      }
    });
  }
}

export const ScopedStorage = new ScopedStorageService();
